import React, {
	RefAttributes,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { react } from "uikit";
import { useTranslation } from "react-i18next";

import MultiSelect from "../LocalMultiSelect";
import { useTypedDispatch, useTypedSelector } from "../../../redux/store";
import getCompanies from "../../../redux/services/Company/getCompanies";
import useObjectEditor from "../../../hooks/useObjectEditor";
import { useTaxiServiceFilterAccessControl } from "../../../hooks";
import LabeledField from "../../LabeledField";

import InternalController from "./Controller";

const CompaniesAndTaxiServicesBase = react.withController<
	CompaniesAndTaxiServicesBase.PropsBase,
	CompaniesAndTaxiServicesBase.Controller
>(
	({
		value,
		onChange,
		controller,

		labelCompany = "settings.tariffs.main.mainModal.tabs.main.company",
		placeholderCompany = "settings.tariffs.main.mainModal.tabs.main.chooseCompany",
		labelTaxiServices = "settings.tariffs.main.mainModal.tabs.main.branch",
		placeholderTaxiServices = "settings.tariffs.main.mainModal.tabs.main.chooseBranch",

		required = true,
		disabled = false,
		autoSelectAllTaxiServices = false,
		setAllIds = false,
		excludeTaxiServiceIds = [],
	}) => {
		const { t } = useTranslation();
		const dispatch = useTypedDispatch();
		const { subCompaniesOptions, subTaxiServiceOptions } =
			useTaxiServiceFilterAccessControl<any>();
		const companies = useTypedSelector(
			(state) => state.ordersPageReducer.companies,
		);

		const [errorCompanyIds, setCompanyIdsError] = useState(false);
		const [errorTaxiServiceIds, setTaxiServiceIdsError] = useState(false);

		const valueEditor = useObjectEditor(value, onChange);

		const taxiServiceIds = valueEditor.useGetter("taxiServiceIds");
		const setTaxiServiceIds = valueEditor.useSetter("taxiServiceIds");

		const selectedCompanies = valueEditor.useGetter("companyIds");
		const setSelectedCompanies = valueEditor.useSetter("companyIds");

		const [initialCompanyIds] = useState(selectedCompanies);
		const [initialTaxiServiceIds] = useState(taxiServiceIds);

		const excludeTaxiServiceIdSet = useMemo(
			() => new Set(excludeTaxiServiceIds),
			[excludeTaxiServiceIds],
		);

		const companyIdByTaxiServiceId = useMemo(() => {
			const map = new Map<number, number>();
			(companies?.items ?? []).forEach((company) => {
				(company.taxiServices ?? []).forEach((service) => {
					if (service.id != null) map.set(service.id, company.id);
				});
			});
			return map;
		}, [companies?.items]);

		const availableTaxiServiceIds = useMemo(() => {
			const options = subTaxiServiceOptions({
				initialTaxiServiceIds,
				initialCompanyIds,
			});
			if (!excludeTaxiServiceIdSet.size) {
				return options.map((option) => option.key);
			}
			return options
				.filter((option) => !excludeTaxiServiceIdSet.has(option.key))
				.map((option) => option.key);
		}, [
			subTaxiServiceOptions,
			initialTaxiServiceIds,
			initialCompanyIds,
			excludeTaxiServiceIdSet,
		]);

		const companiesOptions = useMemo(() => {
			const options = subCompaniesOptions({ initialCompanyIds });
			if (
				!excludeTaxiServiceIdSet.size ||
				!companyIdByTaxiServiceId.size
			) {
				return options;
			}
			const visibleCompanyIds = new Set<number>();
			availableTaxiServiceIds.forEach((taxiServiceId) => {
				const companyId = companyIdByTaxiServiceId.get(taxiServiceId);
				if (companyId != null) visibleCompanyIds.add(companyId);
			});
			return options.filter((option) =>
				visibleCompanyIds.has(option.key),
			);
		}, [
			subCompaniesOptions,
			initialCompanyIds,
			excludeTaxiServiceIdSet,
			companyIdByTaxiServiceId,
			availableTaxiServiceIds,
		]);

		const taxiServiceOptions = useMemo(
			() =>
				subTaxiServiceOptions({
					initialTaxiServiceIds,
					initialCompanyIds,
					selectedCompanies,
				}).filter((option) => !excludeTaxiServiceIdSet.has(option.key)),
			[
				subTaxiServiceOptions,
				initialTaxiServiceIds,
				initialCompanyIds,
				selectedCompanies,
				excludeTaxiServiceIdSet,
			],
		);

		useEffect(() => {
			if (!companies?.items?.length) dispatch(getCompanies());
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [dispatch]);

		useEffect(() => {
			if (setAllIds) {
				const allCompanies: Set<number> = new Set();
				const allTaxiServices: Set<number> = new Set();
				const items = companies?.items?.length ? companies?.items : [];

				for (const item of items) {
					if (item?.id) {
						allCompanies.add(item.id);
						if (item?.taxiServices?.length) {
							for (const ts of item.taxiServices) {
								allTaxiServices.add(ts.id);
							}
						}
					}
				}

				const idsAllCompanies: number[] = [];
				const idsAllTaxiServices: number[] = [];

				allCompanies.forEach((id) => idsAllCompanies.push(id));
				allTaxiServices.forEach((id) => idsAllTaxiServices.push(id));

				setSelectedCompanies(idsAllCompanies);
				setTaxiServiceIds(idsAllTaxiServices);
			}
		}, [
			companies?.items,
			setAllIds,
			setSelectedCompanies,
			setTaxiServiceIds,
		]);

		const updateSelectedValues = useCallback(
			(newCompanies) => {
				setSelectedCompanies(newCompanies);

				if (autoSelectAllTaxiServices) {
					const allTaxiServices = companies.items
						.filter((company) => newCompanies.includes(company.id))
						.flatMap(
							(company) =>
								company.taxiServices?.map(
									(service) => service.id,
								) || [],
						)
						.filter((id) => !excludeTaxiServiceIdSet.has(id));

					setTaxiServiceIds(allTaxiServices);
				}

				if (newCompanies.length) setCompanyIdsError(false);
			},
			[
				setSelectedCompanies,
				setTaxiServiceIds,
				companies.items,
				autoSelectAllTaxiServices,
				excludeTaxiServiceIdSet,
			],
		);

		const updateSelectedTaxiServiceValues = useCallback(
			(ids) => {
				if (ids?.length) setTaxiServiceIdsError(false);

				setTaxiServiceIds([...ids]);
			},
			[setTaxiServiceIds],
		);

		// Defer auto-selection by one render cycle: parents often start with
		// empty useState and sync via useEffect. Without this guard the
		// auto-select fires while currentValueRef still holds empty
		// taxiServiceIds, propagating an onChange that wipes out real data.
		const [ready, setReady] = useState(false);
		useEffect(() => {
			setReady(true);
		}, []);

		useEffect(() => {
			if (disabled) return;
			if (!ready) return;
			if (companiesOptions.length !== 1) return;
			const { key } = companiesOptions[0];
			if (selectedCompanies.includes(key)) return;
			updateSelectedValues([key]);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [companiesOptions, ready]);

		useEffect(() => {
			if (disabled) return;
			if (!ready) return;
			if (taxiServiceOptions?.length !== 1) return;
			const key = taxiServiceOptions[0]?.key ?? -2;
			if (taxiServiceIds.length >= 1) return;
			setTaxiServiceIds([key]);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [taxiServiceOptions, ready]);

		controller.setContext({
			value,
			setCompanyIdsError,
			setTaxiServiceIdsError,
			required,
		});

		return (
			<>
				<LabeledField label={t(labelCompany) || ""}>
					<MultiSelect
						all={companiesOptions.length >= 1}
						disabled={disabled || companiesOptions.length === 1}
						placeholder={t(placeholderCompany) || ""}
						error={errorCompanyIds}
						value={selectedCompanies}
						options={companiesOptions}
						onChange={updateSelectedValues}
					/>
				</LabeledField>
				<LabeledField label={t(labelTaxiServices) || ""}>
					<MultiSelect
						all={(taxiServiceOptions?.length ?? 0) >= 1}
						disabled={disabled || taxiServiceOptions.length <= 1}
						placeholder={t(placeholderTaxiServices) || ""}
						value={taxiServiceIds}
						error={errorTaxiServiceIds}
						options={taxiServiceOptions}
						onChange={updateSelectedTaxiServiceValues}
					/>
				</LabeledField>
			</>
		);
	},
	InternalController,
);

declare namespace CompaniesAndTaxiServicesBase {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface Value {
		taxiServiceIds: number[];
		companyIds: number[];
	}
	interface PropsBase {
		value: Value;
		onChange: (value: Value) => void;

		labelCompany?: string[] | string;
		placeholderCompany?: string[] | string;
		labelTaxiServices?: string[] | string;
		placeholderTaxiServices?: string[] | string;

		/** if required === "false" at that validate === `true` */
		required?: boolean;
		disabled?: boolean;
		autoSelectAllTaxiServices?: boolean;
		setAllIds?: boolean;
		/**
		 * Филиалы, которые уже выбраны в другой записи.
		 * Не показываются в этом селекте — как пользователи,
		 * уже назначенные на другой тариф.
		 */
		excludeTaxiServiceIds?: number[];
	}
	type Props = PropsBase & RefAttributes<Ref>;
}

export const CompaniesAndTaxiServices = memo(CompaniesAndTaxiServicesBase);

declare namespace CompaniesAndTaxiServices {
	interface PropsBase extends CompaniesAndTaxiServicesBase.PropsBase {}
	interface Value extends CompaniesAndTaxiServicesBase.Value {}

	type Ref = CompaniesAndTaxiServicesBase.Ref;
	type Controller = CompaniesAndTaxiServicesBase.Controller;
	type Props = PropsBase & RefAttributes<Ref>;
}

export default CompaniesAndTaxiServicesBase;
